// Settings — Team Members types (Settings-01).

export type TeamMemberStatus =
  | "invited"
  | "active"
  | "suspended"
  | "deactivated";


// Settings-03b — product role-set lives on every team member row.
// A single user can hold multiple roles; the legacy single ``role``
// field is no longer surfaced from the team API.
export type BrokerProductRole = "owner" | "admin" | "member" | "sales";


export interface TeamMember {
  member_id: string;
  user_id: string | null;
  email: string;
  first_name: string;
  last_name: string;
  title: string;
  is_producer: boolean;
  status: TeamMemberStatus;
  // Always present — empty array for unusual states only.
  roles: BrokerProductRole[];
  last_active_at: string | null;
}


/** Pending-invite member rows carry an id of the form
 *  ``invitation:{uuid}``. ``isPendingInvitation`` extracts the
 *  invitation uuid for the resend / revoke endpoints. */
export function isPendingInvitation(row: TeamMember): boolean {
  return row.member_id.startsWith("invitation:");
}


export function invitationIdFor(row: TeamMember): string | null {
  return row.member_id.startsWith("invitation:")
    ? row.member_id.slice("invitation:".length)
    : null;
}


export interface InviteTeamMemberInput {
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  is_producer: boolean;
  // Settings-03b — product role-set assigned on accept. Defaults
  // to ``["member"]`` server-side when omitted, but the FE always
  // sends an explicit selection.
  roles: BrokerProductRole[];
}


export type InvitationPreviewStatus =
  | "ok" | "already_accepted" | "expired" | "revoked";


export interface InvitationPreviewOk {
  status: "ok";
  email: string;
  entity_name: string;
  first_name: string;
  last_name: string;
  title: string;
  is_producer: boolean;
  expires_at: string;
  has_existing_user: boolean;
}


export interface InvitationPreviewAlreadyAccepted {
  status: "already_accepted";
  entity_name?: string;
}


export interface InvitationPreviewExpired {
  status: "expired";
  entity_name?: string;
}


export interface InvitationPreviewRevoked {
  status: "revoked";
  entity_name?: string;
}


export type InvitationPreview =
  | InvitationPreviewOk
  | InvitationPreviewAlreadyAccepted
  | InvitationPreviewExpired
  | InvitationPreviewRevoked;


export interface AcceptInvitationInput {
  password?: string;
  display_name?: string;
}


export interface TeamMemberListResponse {
  rows: TeamMember[];
  total: number;
}


export interface TeamMemberPatchInput {
  first_name?: string;
  last_name?: string;
  title?: string;
  is_producer?: boolean;
  status?: TeamMemberStatus;
  // Settings-03b — full replacement of the role-set. Omit to leave
  // unchanged. Server enforces "at least one role", "owner role
  // requires owner caller", and "cannot remove the final owner".
  roles?: BrokerProductRole[];
}


export interface TitleOptionsResponse {
  titles: string[];
}


// ── Settings-02 — Broker Locations ───────────────────────────────────────


export type BrokerLocationStatus = "active" | "inactive";


export interface BrokerLocation {
  location_id: string;
  name: string;
  /** Derived from ``archived_at`` server-side. ``active`` when
   * ``archived_at IS NULL``; ``inactive`` after a deactivate action.
   * Never user-editable on POST/PATCH. */
  status: BrokerLocationStatus;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  /** Canonical 2-letter US state / territory code from
   * :data:`US_STATES_LIST`. Free-text values are rejected by the
   * backend. */
  state_code: string | null;
  postal_code: string | null;
  archived_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}


export interface BrokerLocationListResponse {
  rows: BrokerLocation[];
  total: number;
}


export interface BrokerLocationCreateInput {
  name: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state_code?: string | null;
  postal_code?: string | null;
}


export interface BrokerLocationPatchInput {
  name?: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state_code?: string | null;
  postal_code?: string | null;
}


// ── Settings-02b — US states reference ───────────────────────────────────


export interface UsState {
  code: string;
  name: string;
}


export interface UsStatesResponse {
  states: UsState[];
}


// ── Settings-02 — Password change ────────────────────────────────────────


export interface PasswordChangeLinkResponse {
  detail: string;
}


export interface PasswordResetConfirmInput {
  token: string;
  new_password: string;
}


// ── Settings-04 — LLM Provider Keys ──────────────────────────────────────


export interface BrokerSettings {
  /** When true the runtime bypasses BYOK and uses the platform's
   * managed Anthropic key (forced to Sonnet 4.6). Default false. */
  use_benecrm_managed_keys: boolean;
  /** BE-SCRM-031 — when on, every closed_lost transition spawns a
   * win/loss interview activity 30 days out. */
  enable_win_loss_interviews?: boolean;
  /** FE-SCRM-055 — firm names that surface at the top of
   * ``/sales/broker-firms/suggestions`` regardless of usage. */
  pinned_broker_firms?: string[];
  /** FE-SCRM-055 — playbook the ``ApplyPlaybookModal`` pre-selects
   * on open. ``null`` when no default is pinned. */
  pinned_default_playbook_id?: string | null;
}


export type ProviderKeyProvider = "anthropic" | "openai" | "gemini";


export type ProviderKeyGateway =
  | "direct" | "azure" | "bedrock" | "vertex";


export interface ProviderKeyRow {
  provider_key_id: string;
  display_name: string;
  provider: ProviderKeyProvider;
  gateway: ProviderKeyGateway;
  is_default: boolean;
  is_active: boolean;
  validation_status: "unvalidated" | "valid" | "invalid" | "expired";
}


export interface ProviderKeyListResponse {
  rows: ProviderKeyRow[];
}


export interface BeneAIAgentSummary {
  agent_id: string;
  slug: string;
  default_model_slug: string;
}


export interface PatchDefaultModelInput {
  slug: string;
}


// Add-key flow — credential-shape schema returned by
// GET /api/v1/agents/provider-keys/credential-shapes.


export interface CredentialField {
  name: string;
  label: string;
  type: "password" | "text";
  required: boolean;
}


export interface CredentialShape {
  provider: ProviderKeyProvider;
  gateway: ProviderKeyGateway;
  label: string;
  fields: CredentialField[];
}


export interface CredentialShapeListResponse {
  shapes: CredentialShape[];
}


export interface CreateProviderKeyInput {
  provider: ProviderKeyProvider;
  gateway: ProviderKeyGateway;
  display_name: string;
  credentials: Record<string, string>;
  is_default?: boolean;
}


/** POST response carries the new row plus, when validation failed,
 * the redacted ``validation_message`` so the FE can show a warning. */
export interface CreateProviderKeyResponse extends ProviderKeyRow {
  validation_message?: string;
}


/** Slugs whitelisted by ``agents_runtime.model_catalog.TIER1_MODELS``.
 * Keep this in sync with the backend catalog. */
export const TIER1_MODEL_SLUGS: ReadonlyArray<string> = [
  "claude-sonnet-4-6",
  "claude-opus-4-7",
  "claude-sonnet-4-6-bedrock",
  "claude-opus-4-7-bedrock",
  "gpt-5",
  "gpt-5-azure",
  "gemini-2.5-pro",
  "gemini-2.5-pro-vertex",
];
